import {
    GET_DEFAULT_CARD_ROUTE,
    getInitialiseOnceOffPaymentRoute,
    getInitialiseAddCardRoute,
    getInitialiseOnceOffBalancePaymentRoute,
} from "@api-routes";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { useFetch } from "@common/hook/useFetch";
import { EPAYMENT_INITIALISE_TYPES, EPAYMENT_TYPES } from "@constants";
import { Button, CircularProgress, Grid, Typography } from "@material-ui/core";
import { getLanguage } from "@utils/changeLanguage";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./css/Payment.scss";

interface ICardPaymentParams {
    serviceId: number;
    isOnceOffPayment: boolean;
    loadDefaultCard: boolean;
    paymentType: EPAYMENT_INITIALISE_TYPES;
    amount?: number;
}

interface IRegisterOrderResponse {
    success: boolean;
    orderId: string;
    formUrl: string;
}

interface IDefaultCard {
    cardholderName: string;
    default: boolean;
    expirationDate: string;
    partialDigits: string;
    paymentMethodId: number;
    paymentMethodType: EPAYMENT_TYPES;
    type: string;
    bindingId: string;
}

const CardPayment = (props: ICardPaymentParams) => {
    const { isOnceOffPayment, loadDefaultCard, paymentType, amount, serviceId } = props;
    const [getStatus, { get }] = useFetch();
    const [orderId, setOrderId] = useState<string>("");
    const [jccForm, setJccForm] = useState<any>();
    const { t } = useTranslation("payment");
    const [submitProcessing, setSubmitProcessing] = useState<boolean>(false);
    const [showSubmitFailedError, setShowSubmitFailedError] = useState<boolean>(false);
    const [bindingCardholder, setBindingCardholder] = useState<string>("");
    const lang = getLanguage();

    const getPaymentInitRoute = (): string => {
        if (paymentType === EPAYMENT_INITIALISE_TYPES.RESET_CREDIT_LIMIT) return getInitialiseOnceOffPaymentRoute(serviceId,lang);
        else if (paymentType === EPAYMENT_INITIALISE_TYPES.MY_BALANCE_PAYMENT)
            return getInitialiseOnceOffBalancePaymentRoute(lang, amount || 0);
        else return getInitialiseAddCardRoute(lang);
    };

    useEffect(() => {
        let jcc: any = null;

        const getDefaultPaymentCardAndInitializeForm = async () => {
            get<IDefaultCard>(GET_DEFAULT_CARD_ROUTE).then((response) => {
                if (response && response.bindingId) {
                    getOrderIdAndInitializeForm(response.bindingId);
                }
            });
        };

        const getOrderIdAndInitializeForm = async (bindingId: string) => {
            get<IRegisterOrderResponse>(getPaymentInitRoute()).then((response) => {
                if (response && response.success) {
                    setOrderId(response.orderId);
                    initializeForm(response.orderId, bindingId);
                }
            });
        };

        const initializeForm = async (orderId: string, bindId: string) => {
            jcc = new (window as any).PaymentForm({
                mdOrder: orderId,
                onFormValidate: (isValid: boolean) => {},
                language: lang,
                containerClassName: "field-container",
                autoFocus: true,
                showPanIcon: true,
                panIconStyle: {
                    height: "16px",
                    top: "calc(50% - 8px)",
                    right: "8px",
                },
                fields: {
                    pan: {
                        container: document.querySelector("#pan"),
                    },
                    expiry: {
                        container: document.querySelector("#expiry"),
                    },
                    cvc: {
                        container: document.querySelector("#cvc"),
                    },
                },
                styles: {
                    base: {
                        color: "black",
                        fontSize: "18px",
                    },
                    valid: { color: "#4a148c", fontWeight: "bold" },
                    invalid: {
                        color: "red",
                        fontWeight: "bold",
                    },
                    placeholder: {
                        base: {
                            color: "gray",
                        },
                        focus: {
                            color: "transparent",
                        },
                    },
                },
            });

            loadDefaultCard
                ? jcc
                      .init()
                      .then(({ orderSession }: any) => {
                          const bindingInfo: any = orderSession.bindings.find((bind: any) => bind.id === bindId);
                          if (bindingInfo) {
                              (document.getElementById("select-binding") as any).options?.add(
                                  new Option(bindingInfo.pan, bindingInfo.id)
                              );
                              (document.getElementById("select-binding") as any).value = bindingInfo.id;
                              jcc.selectBinding(bindingInfo.id);
                              (document.getElementById("cardholderName") as any).value = bindingInfo.cardholderName;
                              setBindingCardholder(bindingInfo.cardholderName);
                          }
                      })
                      .catch((e: any) => {
                          console.log("jcc form init error", e);
                      })
                : jcc
                      .init()
                      .then(() => {})
                      .catch((e: any) => {
                          console.log("jcc form init error", e);
                      });

            setJccForm(jcc);
        };

        loadDefaultCard ? getDefaultPaymentCardAndInitializeForm() : getOrderIdAndInitializeForm("");

        return () => {
            console.log("form to destroy", jcc);
            jcc.selectBinding(null);
            jcc.destroy();
        };
    }, []);

    const handlePayNowClick = async () => {
        if (!/(\w+-){3,10}\w+/g.test(orderId)) {
            console.log("order id is invalid, aborting card save");
            return;
        }

        setShowSubmitFailedError(false);
        setSubmitProcessing(true);

        await jccForm
            .doPayment({
                cardholderName: (document.getElementById("cardholderName") as any).value,
                saveCard: isOnceOffPayment ? false : true,
            })
            .then((result: any) => {
                console.log("doPayment result", result);
            })
            .catch((e: any) => {
                setShowSubmitFailedError(true);
                console.log("doPayment error", e);
            });

        setSubmitProcessing(false);
    };

    return (
        <HandleErrorAndLoading error={getStatus.error} isLoading={getStatus.isLoading}>
            <Grid container alignItems="center" justify="center">
                <Grid alignItems="center" className="payment-container" container item justify="center" xs={12} md={6}>
                    <Grid container spacing={3} direction="row" style={{ padding: "30px 10px" }}>
                        {loadDefaultCard && (
                            <Grid item xs={12}>
                                <Typography color="secondary" variant="h5">
                                    {t("selectCard")}
                                </Typography>
                                <div id="select-binding-container">
                                    <select
                                        className="paymentTextInput"
                                        id="select-binding"
                                        aria-label="Default Card Select"
                                        onChange={(e: any) => {
                                            const binding = e.target.value;
                                            if (binding === "new_card") {
                                                jccForm.selectBinding(null);
                                                (document.getElementById("cardholderName") as any).value = "";
                                            } else {
                                                (document.getElementById(
                                                    "cardholderName"
                                                ) as any).value = bindingCardholder;
                                                jccForm.selectBinding(binding);
                                            }
                                        }}
                                        defaultValue={"new_card"}
                                    >
                                        <option value="new_card">{t("newCard")}</option>
                                    </select>
                                </div>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography color="secondary" variant="h5">
                                {t("cardHolder")}
                            </Typography>
                            <input
                                type="text"
                                id="cardholderName"
                                placeholder={t("cardHolder")}
                                className="paymentTextInput"
                            ></input>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography color="secondary" variant="h5">
                                {t("cardNumber")}
                            </Typography>
                            <div id="pan" className="paymentTextInput"></div>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="secondary" variant="h5">
                                {t("expiryDate")}
                            </Typography>
                            <div id="expiry" className="paymentTextInput"></div>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="secondary" variant="h5">
                                CVC / CVV
                            </Typography>
                            <div id="cvc" className="paymentTextInput"></div>
                        </Grid>
                        <Grid item container alignItems="center" justify="center" spacing={2}>
                            <Grid item>
                                <Button
                                    className="xtest_submit_button"
                                    color="primary"
                                    id="pay"
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    onClick={handlePayNowClick}
                                    disabled={submitProcessing}
                                >
                                    {submitProcessing ? (
                                        <CircularProgress size={20} />
                                    ) : isOnceOffPayment ? (
                                        t("payNow")
                                    ) : (
                                        t("addCard")
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                        {showSubmitFailedError && (
                            <Grid item container alignItems="center" justify="center">
                                <Typography color="error" variant="body1">
                                    {isOnceOffPayment ? t("paymentFailed") : t("saveCardFailed")}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </HandleErrorAndLoading>
    );
};

export default CardPayment;
